import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, Info, CheckCircle } from 'lucide-react';
import apiService from '../../../services/api';

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

interface AnalysisPrompt {
  id: number;
  tier: number;
  countryCode: string;
  country: {
    name: string;
    code: string;
  };
  promptTemplateVersion?: {
    id: number;
    promptTemplate: {
      name: string;
      code: string;
    };
  };
  analysisFramework?: {
    id: number;
    name: string;
    code: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Country {
  code: string;
  name: string;
}

interface PromptTemplate {
  id: number;
  code: string;
  name: string;
  promptVersions?: {
    id: number;
    version: string;
    isActive: boolean;
  }[];
}

interface AnalysisFramework {
  id: number;
  code: string;
  name: string;
}

export default function AnalysisPromptManager() {
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [frameworks, setFrameworks] = useState<AnalysisFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  
  const [editingPrompt, setEditingPrompt] = useState<{
    countryCode: string;
    tier: number;
    templateVersionId: number;
    frameworkId: number;
  } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [promptsRes, countriesRes, templatesRes, frameworksRes] = await Promise.all([
        apiService.get('/api/admin/prompts/analysis-prompts'),
        apiService.get('/api/countries'),
        apiService.get('/api/admin/prompts/templates?includeVersions=true'),
        apiService.get('/api/admin/prompts/frameworks')
      ]);
      
      setPrompts(promptsRes.data);
      setCountries(countriesRes.data);
      setTemplates(templatesRes.data);
      setFrameworks(frameworksRes.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (countryCode: string, tier: number) => {
    const prompt = prompts.find(p => p.countryCode === countryCode && p.tier === tier);
    if (prompt) {
      setEditingPrompt({
        countryCode,
        tier,
        templateVersionId: prompt.promptTemplateVersion?.id || 0,
        frameworkId: prompt.analysisFramework?.id || 0
      });
    } else {
      // Create new
      const defaultTemplate = templates.find(t => t.promptVersions?.[0])?.promptVersions?.[0];
      const defaultFramework = frameworks[0];
      
      setEditingPrompt({
        countryCode,
        tier,
        templateVersionId: defaultTemplate?.id || 0,
        frameworkId: defaultFramework?.id || 0
      });
    }
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    try {
      await apiService.post('/api/admin/prompts/analysis-prompts', {
        tier: editingPrompt.tier,
        countryCode: editingPrompt.countryCode,
        promptTemplateVersionId: editingPrompt.templateVersionId,
        analysisFrameworkId: editingPrompt.frameworkId
      });
      
      setNotification({ type: 'success', message: 'Analysis prompt saved successfully' });
      setEditingPrompt(null);
      fetchData();
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save analysis prompt' });
    }
  };

  const tiers = [1, 2, 3];

  return (
    <div className="p-6">
      {/* Help Section */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
          <Info className="w-5 h-5" />
          What are Active Analysis Prompts?
        </h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          Active Analysis Prompts combine templates and frameworks for each country and analysis tier:
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
          <li>• <strong>Tier 1</strong>: Quick relevance check (Gemini Flash) - filters out ~90% of articles - <em>No framework needed</em></li>
          <li>• <strong>Tier 2</strong>: Standard deep analysis (Claude Sonnet) - for relevant articles - <em>Requires framework</em></li>
          <li>• <strong>Tier 3</strong>: Extended analysis with more tokens - for critical articles - <em>Requires framework</em></li>
        </ul>
        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Important:</strong> Tier 1 only needs a template (relevance check) as it's a simple yes/no filter. 
            Tiers 2 & 3 require both a template AND a framework for full threat analysis. 
            If nothing is selected, the prompt is considered unconfigured and won't be used.
          </p>
        </div>
      </div>

      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {notification.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading analysis prompts...</div>
      ) : (
        <div className="space-y-6">
          {countries.map(country => (
            <div key={country.code} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {country.name} ({country.code})
                </h4>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tiers.map(tier => {
                    const prompt = prompts.find(p => p.countryCode === country.code && p.tier === tier);
                    const isEditing = editingPrompt?.countryCode === country.code && editingPrompt?.tier === tier;
                    
                    return (
                      <div key={tier} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                          Tier {tier}
                        </h5>
                        
                        {isEditing ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Prompt Template
                              </label>
                              <select
                                value={editingPrompt.templateVersionId}
                                onChange={(e) => setEditingPrompt({
                                  ...editingPrompt,
                                  templateVersionId: parseInt(e.target.value)
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="0">Select template...</option>
                                {templates.map(template => 
                                  template.promptVersions?.map(version => (
                                    <option key={version.id} value={version.id}>
                                      {template.name} (v{version.version})
                                    </option>
                                  ))
                                )}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Analysis Framework
                              </label>
                              <select
                                value={editingPrompt.frameworkId}
                                onChange={(e) => setEditingPrompt({
                                  ...editingPrompt,
                                  frameworkId: parseInt(e.target.value)
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="0">Select framework...</option>
                                {frameworks.map(framework => (
                                  <option key={framework.id} value={framework.id}>
                                    {framework.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={handleSave}
                                className="flex-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              >
                                <Save className="w-3 h-3 inline mr-1" />
                                Save
                              </button>
                              <button
                                onClick={() => setEditingPrompt(null)}
                                className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {prompt ? (
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">Template:</p>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {prompt.promptTemplateVersion?.promptTemplate.name || 'None'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">Framework:</p>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {prompt.analysisFramework?.name || 'None'}
                                  </p>
                                </div>
                                <div className="pt-2">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    prompt.isActive 
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                  }`}>
                                    {prompt.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Not configured
                              </p>
                            )}
                            
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => handleEdit(country.code, tier)}
                                className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                              >
                                <Settings className="w-3 h-3" />
                                {prompt ? 'Edit' : 'Configure'}
                              </button>
                              {prompt && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await apiService.put(`/api/admin/prompts/analysis-prompts/${prompt.id}`, {
                                        isActive: !prompt.isActive
                                      });
                                      setNotification({ 
                                        type: 'success', 
                                        message: prompt.isActive 
                                          ? 'Prompt deactivated successfully' 
                                          : `Prompt activated successfully. Other tier ${tier} prompts for ${country.name} have been deactivated.`
                                      });
                                      fetchData();
                                    } catch (error) {
                                      setNotification({ type: 'error', message: 'Failed to update prompt status' });
                                    }
                                  }}
                                  className={`px-3 py-1.5 text-xs rounded transition-colors ${
                                    prompt.isActive 
                                      ? 'bg-red-600 text-white hover:bg-red-700'
                                      : 'bg-green-600 text-white hover:bg-green-700'
                                  }`}
                                >
                                  {prompt.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}